import React, {useMemo, useImperativeHandle, forwardRef, useEffect} from "react";
import clsx from "clsx";
import OverviewListTable from "@Local/Components/OverviewList/OverviewListTable";
import { Checkbox, TextField, Typography } from '@curves/ui-base';

import {ProductCard} from '@Local/Components/InfoCard';
import useSalesAllowance from "@Local/Orders/Componetnets/SalesAllowanceItemTable/useSalesAllowance";
import {formatCurrencyFn, isFunction} from "@Util/utils";
/**
 * SalesAllowanceItemTable
 *
 * 訂單「逐項折讓」清單元件（可切換未選取/已選取兩種展示模式）。
 * - 未選取模式：純展示（單價/數量/已折讓/小計/備註）。
 * - 已選取模式：可編輯（依商品類型顯示「可折讓金額」或「可折讓數量」），即時計算列小計與總計。
 *
 * 與「結算層」互動：
 * - 透過 `onTotalChange(total:number)` 對外上拋目前清單合計（grandTotal）。
 * - 透過 `onValidityChange(hasError:boolean)` 對外上拋目前清單是否存在輸入錯誤。
 * - 供父層以 `ref` 取得 `getResult()`（僅包含被勾選的 line items）與 `getTotal()`（可退總計）。
 *
 * 折讓規則（重點）：
 * - `onlyAllowancePrice === true`：僅允許調整金額（qty 固定 1），金額範圍 [1, salePrice]；
 * - `onlyAllowancePrice === false`：僅允許調整數量（價鎖 `salePrice`），數量範圍 [1, quantity - returnQuantity]；
 * - 已全額退過者不可再選（價：`salePrice === returnPrice`；量：`quantity - returnQuantity <= 0`）。
 * - 勾選 ↔ 取消時，元件會將該列的金額/數量「重置為預設值」，避免殘留舊輸入。
 *
 * 呈現：
 * - 使用 OverviewListTable 作為表格容器，表頭/列渲染由 `headerRow` 動態產生。
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - 外層自訂 class
 * @param {Array<Object>} [props.items=[]] - 商品列資料
 * @param {boolean} [props.listDisabled=false] - 停用整份清單（checkbox 與輸入欄位均禁用）
 * @param {Object} [props.overviewProps={}] - 透傳給 OverviewListTable 的屬性（分頁、樣式等）
 * @param {Function} [props.onTotalChange] - 列合計變動時回呼；簽章 `(total:number) => void`
 * @param {Function} [props.onValidityChange] - 清單輸入錯誤狀態變動時回呼；簽章 `(hasError:boolean) => void`
 *
 * @ref
 * 父層可透過 `ref` 存取以下方法：
 * - `getResult(): Array<{ orderItemID: number|string, orderID: number|string, allowancePrice: number, allowanceQuantity: number }>`
 *    僅回傳「被勾選」列；金額/數量依當前輸入值（若無輸入則取預設值）。
 * - `isError(): boolean`
 *    是否存在任一列輸入錯誤（越界等）。
 * - `getTotal(): number`
 *    回傳目前清單合計（grandTotal）。
 *
 * @example
 * // 在父層使用
 * const tableRef = useRef(null);
 * <SalesAllowanceItemTable
 *   ref={tableRef}
 *   items={orderItems}
 *   onTotalChange={(t) => setGroupTotal(t)}
 *   onValidityChange={(bad) => setGroupHasError(bad)}
 * />
 */
const SalesAllowanceItemTable = forwardRef(
    ({
         className,
         items = [],
         listDisabled = false,
         overviewProps = {},
         onTotalChange,
         onValidityChange
     }, ref) => {
        const {
            selectedIds,
            priceById,
            qtyById,
            errorById,
            isAllChecked,
            isIndeterminate,
            showSelectedMode,
            grandTotal,
            toggleAll,
            toggleOne,
            onChangePrice,
            onChangeQty,
            rowTotal,
            boundsOf,
            isSelectable,
            getId,
        } = useSalesAllowance(items, {listDisabled});
        useEffect(() => {
                isFunction(onTotalChange) && onTotalChange(grandTotal);
            },
            // eslint-disable-next-line
            [grandTotal, onTotalChange]);
        useEffect(() => {
                const hasError = Object.values(errorById).some(Boolean);
                isFunction(onValidityChange) && onValidityChange(hasError);
            },
            // eslint-disable-next-line
            [errorById, onValidityChange]);
        const headerRow = useMemo(() => {
                const base = [
                    {
                        cellKey: "sys:select",
                        headerLabel: (
                            <Checkbox
                                checked={isAllChecked}
                                indeterminate={isIndeterminate}
                                onChange={(e, ck) => toggleAll(ck)}
                                disabled={listDisabled}
                            />
                        ),
                        minWidth: 48,
                        className: "vertical-align-middle",
                        formatCell: (_v, row) => {
                            const id = getId(row);
                            const checked = selectedIds.has(id);
                            return (
                                <Checkbox
                                    checked={checked}
                                    onChange={(e, ck) => toggleOne(id, ck, row)}
                                    disabled={!isSelectable(row, {listDisabled})}
                                />
                            );
                        },
                    },
                    {
                        cellKey: "product",
                        headerLabel: "品項",
                        minWidth: 200,
                        className: "vertical-align-middle",
                        formatCell: (_v, row) => <ProductCard productName={row.productName} pictureSrc={row.pictureSrc}/>,
                    },
                    {
                        cellKey: "type",
                        headerLabel: "類型",
                        align: "center",
                        minWidth: 160,
                        className: "vertical-align-middle",
                        formatCell: (_v, row) => (
                                <Typography className="mb-0">{row.productTypeText}</Typography>
                        ),
                    },
                ];
                if (!showSelectedMode) {
                    // 未選取模式（只讀）
                    return [
                        ...base,
                        {
                            cellKey: "salePrice",
                            headerLabel: "單價",
                            align: "right",
                            minWidth: 120,
                            className: "vertical-align-middle",
                            formatCell: (_v, row) => (
                                    <Typography className="mb-0">{formatCurrencyFn(row.salePrice)}</Typography>
                            ),
                        },
                        {
                            cellKey: "quantity",
                            headerLabel: "數量",
                            align: "center",
                            minWidth: 100,
                            className: "vertical-align-middle",
                            formatCell: (_v, row) => (row.onlyAllowancePrice ? "" : row.quantity),
                        },
                        {
                            cellKey: "returnQuantity",
                            headerLabel: "已折讓數量",
                            align: "center",
                            minWidth: 120,
                            className: "vertical-align-middle",
                            formatCell: (_v, row) => (row.onlyAllowancePrice ? "" : row.returnQuantity ?? 0),
                        },
                        {
                            cellKey: "originTotal",
                            headerLabel: "小計",
                            align: "right",
                            minWidth: 140,
                            className: "vertical-align-middle",
                            formatCell: (_v, row) => formatCurrencyFn((Number(row.salePrice) || 0) * (Number(row.quantity) || 0)),
                        },
                        {
                            cellKey: "returnPrice",
                            headerLabel: "已折讓金額",
                            align: "right",
                            minWidth: 140,
                            className: "vertical-align-middle",
                            formatCell: (_v, row) => formatCurrencyFn(Number(row.returnPrice) || 0),
                        },
                        {
                            cellKey: "notes",
                            headerLabel: "備註",
                            minWidth: 160,
                            className: "vertical-align-middle",
                            formatCell: (_v, row) => row.notes || "",
                        },
                    ];
                }
                // 已選取模式（可編）
                return [
                    ...base,
                    {
                        cellKey: "editablePrice",
                        headerLabel: "可折讓金額",
                        align: "center",
                        minWidth: 160,
                        className: "vertical-align-middle",
                        formatCell: (_v, row) => {
                            const id = getId(row);
                            const checked = selectedIds.has(id);
                            const b = boundsOf(row);
                            if (row.onlyAllowancePrice) {
                                const value = priceById[id] ?? b.defaultPrice;
                                const hasError = errorById[id] === true;
                                return (
                                    <TextField
                                        maskType="MONEY"
                                        value={value}
                                        inputProps={{placeholder: row.salePrice, max: row.salePrice, min: 1}}
                                        disabled={!checked}
                                        error={hasError}
                                        onChangeNative={(_, val) => onChangePrice(row, val)}
                                    />
                                );
                            }
                            // 可調數量時，單價顯示為鎖定
                            return formatCurrencyFn(row.salePrice);
                        },
                    },
                    {
                        cellKey: "editableQty",
                        headerLabel: "可折讓數量",
                        align: "center",
                        minWidth: 160,
                        className: "vertical-align-middle",
                        formatCell: (_v, row) => {
                            if (row.onlyAllowancePrice) return "";
                            const id = getId(row);
                            const checked = selectedIds.has(id);
                            const b = boundsOf(row);
                            const value = qtyById[id] ?? b.defaultQty;
                            const hasError = errorById[id] === true;
                            return (
                                <TextField
                                    maskType="MONEY"
                                    value={value}
                                    inputProps={{placeholder: row.quantity, max: b.qtyMax, min: 1}}
                                    disabled={!checked}
                                    error={hasError}
                                    onChangeNative={(_, val) => onChangeQty(row, val)}
                                />
                            );
                        },
                    },
                    {
                        cellKey: "adjustTotal",
                        headerLabel: <strong>折讓小計</strong>,
                        align: "right",
                        minWidth: 160,
                        className: "vertical-align-middle",
                        formatCell: (_v, row) => <strong>{formatCurrencyFn(rowTotal(row))}</strong>,
                    },
                    {
                        cellKey: "notes",
                        headerLabel: "備註",
                        minWidth: 160,
                        className: "vertical-align-middle",
                        formatCell: (_v, row) => row.notes || "",
                    },
                ];
            },
            // eslint-disable-next-line
            [
                showSelectedMode,
                isAllChecked,
                isIndeterminate,
                toggleAll,
                toggleOne,
                selectedIds,
                priceById,
                qtyById,
                listDisabled,
                errorById,
                rowTotal,
            ]);
        useImperativeHandle(
            ref,
            () => ({
                getResult: () => {
                    const results  = [];
                    items.forEach((row) => {
                        const id = getId(row);
                        if (!selectedIds.has(id)) return;
                        const b = boundsOf(row);
                        const allowancePrice = row.onlyAllowancePrice ? (priceById[id] || b.defaultPrice) : row.salePrice;
                        const allowanceQuantity = row.onlyAllowancePrice ? 1 : (qtyById[id] || b.defaultQty);
                        results.push({ orderItemID: row.orderItemID, orderID: row.orderID, allowancePrice, allowanceQuantity });
                    })
                    return results;
                },
                isError: () => Object.values(errorById).some(Boolean),
                getTotal: () => grandTotal,
            }),
            // eslint-disable-next-line
            [items, selectedIds, priceById, qtyById, errorById, grandTotal]
        );
        return (
            <OverviewListTable
                className={clsx("sales-allowance-item-table", className)}
                headerRow={headerRow}
                list={items}
                isPagination={overviewProps.isPagination || overviewProps.isMasonryPagination || false}
                isMasonryPagination={overviewProps.isMasonryPagination || false}
                onChangePage={overviewProps.onChangePage}
                pageIndex={overviewProps.pageIndex}
                totalPage={overviewProps.totalPage}
                isGray={overviewProps.isGray ?? true}
                tableRowHeaderProps={overviewProps.tableRowHeaderProps}
                tableRowBodyProps={overviewProps.tableRowBodyProps}
            />
        );
    }
);
export default SalesAllowanceItemTable;
